import React, { useEffect, useMemo } from 'react';
import { Block } from "@randajan/jet-react/dom/block";

import "./Events.scss";
import { useBeam } from '@randajan/bifrost/client/react';
import { usePop } from '@randajan/jet-react/dom/modal';
import { page } from '@randajan/jet-react/base/page';
import { Router } from '..';
import { EventDetail } from './EventDetail/EventDetail';
import { DataView } from '../../elements/DataView/DataView';
import { Locale } from '../../assets/Locale';
import { LocaleFlag } from '../../frames/Locales/LocaleFlag/LocaleFlag';
import { EventImage } from './parts/EventImage/EventImage';
import { EventDate } from './parts/EventDate/EventDate';
import { EventUserCount } from './parts/EventUserCount/EventUserCount';
import { EventAvMode } from './parts/EventAvMode/EventAvMode';
import { EventDetailOpen } from './parts/EventDetailOpen/EventDetailOpen';
import { screen } from '@randajan/jet-react/base/screen';
import { eventsBeam } from '../../assets/beams/eventsBeam';
import { sayEventAvMode, sayEventStatus } from './eventSay';
import { EventCard } from './EventCard/EventCard';


const defaultFilters = { status:["published", "started"] };
const defaultSorting = [{ id:"startAtEst", desc:false }];

const generateColumns = (say) => {
    const list = [];

    list.push({
        id: "webImg",
        header: null,
        enableSearch: false,
        enableSorting: false,
        renderAs: value => <EventImage value={value}/>
    });

    list.push({
        id: "name",
        header: say("eventKindEvent"),
        accessAs: (value, row) => value || row.gameSpace?.name || ""
    });

    list.push({
        id: "startAtEst",
        header: say("eventListDate"),
        sortAs: v => new Date(v).getTime(),
        renderAs: (v, row, ctx) => <EventDate value={v} row={row} isCompact={ctx.view==="table"} />,
        searchAs: (v, row) => `${say.dateTime(v)} ${say.duration(row.durationEst)}`
    });

    list.push({
        id: "status",
        header: say("eventListStatus"),
        filter: "multiSelect",
        renderAs: v => sayEventStatus(say, v),
        searchAs: v => sayEventStatus(say, v),
        options: [
            { value: "draft", label: sayEventStatus(say, "draft") },
            { value: "published", label: sayEventStatus(say, "published") },
            { value: "started", label: sayEventStatus(say, "started") },
            { value: "ended", label: sayEventStatus(say, "ended") },
            { value: "completed", label: sayEventStatus(say, "completed") },
            { value: "canceled", label: sayEventStatus(say, "canceled") }
        ]
    });

    list.push({
        id: "locale",
        header: say("eventListLanguage"),
        filter: "select",
        renderAs: v => <LocaleFlag locale={v} />
    });
    list.push({
        id: "avMode",
        header: say("eventListAvMode"),
        filter: "multiSelect",
        options: ["av-a", "av-b", "av-c"].map(value=>({
            value,
            label:sayEventAvMode(say, value)
        })),
        renderAs: (v, row) => !v?.length ? null : <EventAvMode value={v} row={row} />
    });

    list.push({
        id: "gameEventUsers",
        header: say("eventListCapacity"),
        searchAs: (v, { maxUsers }) => `${v}/${maxUsers}`,
        renderAs: (v, row) => <EventUserCount value={v} row={row} />
    });

    list.push({
        id: "id",
        header: null,
        enableSorting: false,
        renderAs: (value, row) => <EventDetailOpen value={value} row={row} />
    })

    return list;
}

const renderEventCard = row=><EventCard event={row.original}/>;

export const Events = (props) => {
    const { } = props;
    const say = Locale.use();

    const { eventId } = Router.useParams();

    const { data: events } = useBeam(eventsBeam);
    const [ wideScreen ] = screen.use("1280w");
    const forceView = !wideScreen.get() && "grid";

    const columns = useMemo(_ => generateColumns(say), [say]);

    const pop = usePop({
        closeOnBlur: false,
        onDown: _ => {
            const pathname = page.get("pathname");
            if (pathname.startsWith("/events")) { page.set("pathname", "/events"); }
        }
    });

    useEffect(_ => {
        if (!pop) { return; }
        if (!eventId) { pop.down(); }
        else { pop.up(<EventDetail eventId={eventId} />); }
    }, [pop, eventId]);

    return (
        <Block level={0} className="Events" caption={say("navEvents")}>
            <section className="slab wall -dark -border">
                <DataView
                    virtualAbove={0}
                    virtualOffset={-0.1}
                    transition={250}
                    defaultFilters={defaultFilters}
                    defaultSorting={defaultSorting}
                    defaultView="grid"
                    forceView={forceView}
                    say={say}
                    data={events}
                    columns={columns}
                    renderCard={renderEventCard}
                />
            </section>
        </Block>
    )
}
